
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Fab,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Avatar,
  Stack, // For message layout
  Chip, // For option chips
  Link, // <<< Added Link import
} from "@mui/material";
import { Chat, Close, Send, SupportAgent, Person } from "@mui/icons-material";

const Chatbot = ({ courses = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0); // 0: Welcome, 1: Ask Name, 2: Ask Email, 3: Ask Contact, 4: Show Options, 5: Ask Specific Program
  const [userData, setUserData] = useState({ name: "", email: "", contact: "" });
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isBotTyping, setIsBotTyping] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const initialBotMessage = "Hello! I'm your friendly assistant. I can help you with information about our programs.";
  const askNameMessage = "To get started, could you please tell me your name?";
  const askEmailMessage = (name) => `Nice to meet you, ${name}! What's your email address? We'll use this to send you relevant information.`;
  const askContactMessage = "Great! And your contact number, please? This helps us reach out if needed.";
  const thankYouAndShowOptionsMessage = "Thank you for the information! How can I assist you further today? Here are some options:";
  const askWhichProgramMessage = "Sure! Which program are you interested in? You can type the name or select from a list if available.";

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, step]);

  // eslint-disable-next-line
  const resetChat = () => {
    setMessages([]);
    setUserData({ name: "", email: "", contact: "" });
    setInputValue("");
    setStep(0);
  };


  const addMessage = (sender, text, type = "text", options = []) => {
    setMessages((prevMessages) => [
      ...prevMessages,
      { sender, text, type, options, timestamp: new Date() },
    ]);
    scrollToBottom();
  };

  const handleBotResponse = (userMessageText = "") => {
    setIsBotTyping(true);
    setTimeout(() => {
      setIsBotTyping(false);
      switch (step) {
        case 0: // Initial welcome
          addMessage("bot", initialBotMessage);
          setTimeout(() => addMessage("bot", askNameMessage), 600);
          setStep(1);
          break;
        case 1: // Expecting name
          const name = userMessageText.trim();
          if (name.length < 2) {
            addMessage("bot", "Please enter a valid name (at least 2 characters).");
            return;
          }
          setUserData((prev) => ({ ...prev, name }));
          addMessage("bot", askEmailMessage(name));
          setStep(2);
          break;
        case 2: // Expecting email
          const email = userMessageText.trim();
          if (!/\S+@\S+\.\S+/.test(email)) {
            addMessage("bot", "That doesn't look like a valid email. Please try again (e.g., user@example.com).");
            return;
          }
          setUserData((prev) => ({ ...prev, email }));
          addMessage("bot", askContactMessage);
          setStep(3);
          break;
        case 3: // Expecting contact
          const contact = userMessageText.trim().replace(/\D/g, '');
          if (!/^\d{10,15}$/.test(contact)) {
            addMessage("bot", "Please enter a valid contact number (10-15 digits).");
            return;
          }
          setUserData((prev) => ({ ...prev, contact }));
          addMessage("bot", thankYouAndShowOptionsMessage, "options", generateMainOptions());
          setStep(4);
          break;
        case 4:
            addMessage("bot", `I've noted your query: "${userMessageText}". If this doesn't fit an option, one of our team members may follow up. You can also select an option.`);
            setTimeout(() => addMessage("bot", "Here are the main options again:", "options", generateMainOptions()), 800);
            break;
        case 5:
            const programQuery = userMessageText.trim().toLowerCase();
            if (!programQuery) {
                addMessage("bot", "Please type the name of the program you're interested in.");
                return;
            }
            const matchedPrograms = courses.filter(c => c.program_name && c.program_name.toLowerCase().includes(programQuery));
            if (matchedPrograms.length === 1) {
                const prog = matchedPrograms[0];
                addMessage("bot", `Okay, about ${prog.program_name}: It's a ${prog.duration || 'N/A'} program conducted in ${prog.mode || 'N/A'} mode. Would you like to apply or download its brochure?`, "options", [
                    { text: `Apply for ${prog.program_name}`, action: "apply_specific", program: prog },
                    { text: `Brochure for ${prog.program_name}`, action: "brochure_specific", program: prog }
                ]);
                setTimeout(() => { addMessage("bot", "What else can I help you with?", "options", generateMainOptions()); setStep(4); }, 1200);

            } else if (matchedPrograms.length > 1) {
                addMessage("bot", "I found a few programs matching that. Which one did you mean?", "options",
                    matchedPrograms.map(p => ({ text: p.program_name, action: "select_specific_from_list", program: p }))
                );
            } else {
                addMessage("bot", `Sorry, I couldn't find a program named "${userMessageText}". Please check the spelling or try "List all programs" to see available options.`);
                setTimeout(() => {addMessage("bot", "What would you like to do next?", "options", generateMainOptions()); setStep(4);}, 800);
            }
            break;
        default:
          addMessage("bot", "I'm not sure how to respond to that. Let's try an option.");
          addMessage("bot", "How can I help you?", "options", generateMainOptions());
          setStep(4);
      }
    }, 800);
  };


  const generateMainOptions = () => {
    let options = [
      { text: "List All Programs", action: "list_programs" },
      { text: "About a Specific Program", action: "ask_specific_program" },
      { text: "How to Apply?", action: "how_to_apply" },
      { text: "Support Email Address?", action: "support_email" },
      { text: "General Inquiry", action: "general_inquiry" },
    ];
    return options;
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    const messageText = inputValue.trim();
    if (!messageText) return;

    addMessage("user", messageText);
    setInputValue("");
    handleBotResponse(messageText);
  };


  const handleOptionClick = (option) => {
    addMessage("user", `Selected: ${option.text}`, "option_click");
    setIsBotTyping(true);
    setTimeout(() => {
        setIsBotTyping(false);
        switch (option.action) {
            case "list_programs":
                if (courses && courses.length > 0) {
                    let programList = "Here are our available programs:\n";
                    courses.forEach((course, index) => {
                        programList += `${index + 1}. ${course.program_name || 'Unnamed Program'}\n`;
                    });
                    addMessage("bot", programList);
                } else {
                    addMessage("bot", "We currently don't have a list of programs here. Please check the main page or ask a general question.");
                }
                setTimeout(() => addMessage("bot", "What else can I help you with?", "options", generateMainOptions()), 800);
                setStep(4);
                break;
            case "ask_specific_program":
                addMessage("bot", askWhichProgramMessage);
                if(courses && courses.length > 0 && courses.length <= 5) {
                    addMessage("bot", "You can choose from these or type a name:", "options",
                        courses.map(p => ({ text: p.program_name || 'Unnamed Program', action: "select_specific_from_list", program: p }))
                    );
                }
                setStep(5);
                break;
            case "select_specific_from_list":
                const prog = option.program;
                 addMessage("bot", `Okay, about ${prog.program_name || 'this program'}: It's a ${prog.duration || 'N/A'} program conducted in ${prog.mode || 'N/A'} mode. Would you like to apply or get the brochure?`, "options", [
                    { text: `Apply for this program`, action: "apply_specific", program: prog },
                    { text: `Download brochure`, action: "brochure_specific", program: prog }
                ]);
                setTimeout(() => { addMessage("bot", "What else can I help you with?", "options", generateMainOptions()); setStep(4); }, 1200);
                break;
            case "how_to_apply":
                addMessage("bot", "To apply for a program, please find the 'Apply Now' button on the program card on our main page. This will open an application form.");
                setTimeout(() => addMessage("bot", "Anything else?", "options", generateMainOptions()), 800);
                setStep(4);
                break;
            case "support_email":
                addMessage("bot",
                    <>
                        For any support inquiries, please email us at{" "}
                        <Link
                            href="mailto:empower@tdtl.world"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: '#6a1b9a', // Using a theme-consistent color
                                fontWeight: 'bold',
                                textDecoration: 'underline',
                                '&:hover': {
                                    textDecoration: 'underline',
                                }
                            }}
                        >
                            empower@tdtl.world
                        </Link>.
                    </>
                );
                setTimeout(() => addMessage("bot", "Is there anything else I can assist with?", "options", generateMainOptions()), 1000);
                setStep(4);
                break;
            case "general_inquiry":
                addMessage("bot", "Please type your general question, and I'll do my best to assist or forward it to our support team.");
                break;
            case "apply_specific":
            case "brochure_specific":
                addMessage("bot", `Great! For actions related to "${option.program?.program_name || 'this program'}", please use the buttons on the main page. I can't directly open forms or download files from here.`);
                console.log("Chatbot action requested:", option);
                setTimeout(() => { addMessage("bot", "What else can I help you with?", "options", generateMainOptions()); setStep(4); }, 1000);
                break;
            default:
                addMessage("bot", "I'm processing that. One moment.");
                setTimeout(() => { addMessage("bot", "How can I help you today?", "options", generateMainOptions()); setStep(4); }, 1000);
        }
    }, 600);
  };


  const toggleChatbot = () => {
    const newIsOpenState = !isOpen;
    setIsOpen(newIsOpenState);
    if (newIsOpenState && messages.length === 0) {
      handleBotResponse();
    } else if (newIsOpenState) {
        setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  return (
    <>
      <Fab
        onClick={toggleChatbot}
        aria-label={isOpen ? "close chat" : "open chat"}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1301,
          background: "linear-gradient(45deg, #6a1b9a 30%, #ec407a 90%)",
          color: "white",
          boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
          transition: 'transform 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.1)',
          }
        }}
      >
        {isOpen ? <Close /> : <Chat />}
      </Fab>

      {isOpen && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: { xs: 0, sm: 90 },
            right: { xs: 0, sm: 24 },
            width: { xs: "100vw", sm: 370 },
            height: { xs: "100vh", sm: 520 },
            maxHeight: { xs: "100%", sm: "calc(100vh - 100px)"},
            zIndex: 1300,
            display: "flex",
            flexDirection: "column",
            borderRadius: { xs: 0, sm: "12px" },
            overflow: "hidden",
            boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
            transition: 'opacity 0.3s ease, transform 0.3s ease, bottom 0.3s ease',
            opacity: 1,
            transform: 'translateY(0)',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              padding: "12px 16px",
              background: "linear-gradient(45deg, #6a1b9a 30%, #ec407a 90%)",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTopLeftRadius: { sm: "12px" },
              borderTopRightRadius: { sm: "12px" },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
                <SupportAgent />
                <Typography variant="h6" component="div" sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                Support Chat
                </Typography>
            </Stack>
            <IconButton onClick={toggleChatbot} size="small" sx={{ color: "white" }} aria-label="close chat">
              <Close />
            </IconButton>
          </Box>

          {/* Messages Area */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              padding: "16px",
              backgroundColor: "#f4f6f8",
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {messages.map((msg, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: msg.sender === "user" ? "flex-end" : "flex-start",
                  marginBottom: "12px",
                  width: '100%',
                }}
              >
                <Stack direction={msg.sender === "user" ? "row-reverse" : "row"} spacing={1} alignItems="flex-start" sx={{maxWidth: "85%"}}>
                  <Avatar sx={{ bgcolor: msg.sender === "user" ? "#ec407a" : "#6a1b9a", width: 32, height: 32, fontSize: '0.9rem' }}>
                    {msg.sender === "user" ? <Person fontSize="small"/> : <SupportAgent fontSize="small"/>}
                  </Avatar>
                  <Paper
                    elevation={1}
                    sx={{
                      padding: "10px 14px",
                      borderRadius: msg.sender === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                      backgroundColor: msg.sender === "user" ? "#6a1b9a" : "#ffffff",
                      color: msg.sender === "user" ? "white" : "rgba(0,0,0,0.87)",
                      wordWrap: "break-word",
                      whiteSpace: 'pre-wrap',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    }}
                  >
                    {/* Render msg.text which can now be a string or JSX (like the Link) */}
                    {msg.text}
                    {msg.type === "options" && msg.options && msg.options.length > 0 && (
                        <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                            {msg.options.map((opt, i) => (
                                <Chip
                                    key={i}
                                    label={opt.text}
                                    onClick={() => handleOptionClick(opt)}
                                    clickable
                                    size="small"
                                    sx={{
                                        backgroundColor: 'rgba(106, 27, 154, 0.1)',
                                        color: '#5e1687',
                                        fontWeight: 500,
                                        justifyContent: 'flex-start',
                                        '&:hover': {
                                            backgroundColor: 'rgba(106, 27, 154, 0.2)',
                                        }
                                    }}
                                />
                            ))}
                        </Stack>
                    )}
                  </Paper>
                </Stack>
              </Box>
            ))}
            {isBotTyping && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: '12px' }}>
                    <Avatar sx={{ bgcolor: "#6a1b9a", width: 32, height: 32, mr:1 }}>
                        <SupportAgent fontSize="small"/>
                    </Avatar>
                    <Paper elevation={1} sx={{p:'10px 14px', borderRadius: "18px 18px 18px 4px", backgroundColor: "#ffffff", display: 'flex', alignItems: 'center'}}>
                        <CircularProgress size={18} sx={{ color: '#6a1b9a' }}/>
                        <Typography variant="body2" sx={{ml: 1, color: 'rgba(0,0,0,0.6)'}}>Typing...</Typography>
                    </Paper>
                </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Box
            component="form"
            onSubmit={handleSendMessage}
            sx={{
              display: "flex",
              padding: "12px",
              borderTop: "1px solid #e0e0e0",
              backgroundColor: "white",
              borderBottomLeftRadius: { sm: "12px" },
              borderBottomRightRadius: { sm: "12px" },
            }}
          >
            <TextField
              inputRef={inputRef}
              fullWidth
              variant="outlined"
              size="small"
              placeholder={
                step <= 0 || isBotTyping ? "Waiting for bot..." :
                step === 1 ? "Type your name..." :
                step === 2 ? "Type your email..." :
                step === 3 ? "Type your contact number..." :
                "Type your message or choose an option..."
              }
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              disabled={isBotTyping || step === 0}
              sx={{
                mr: 1,
                '& .MuiOutlinedInput-root': {
                    borderRadius: '20px',
                    backgroundColor: '#f4f6f8',
                    '& fieldset': {
                        borderColor: '#e0e0e0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#b0b0b0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#6a1b9a',
                      borderWidth: '1px',
                    },
                  },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isBotTyping || !inputValue.trim()}
              aria-label="send message"
              sx={{
                minWidth: "48px",
                height: "40px",
                borderRadius: "50%",
                padding: 0,
                background: "linear-gradient(45deg, #6a1b9a 30%, #ec407a 90%)",
                boxShadow: 'none',
                '&:hover': {
                    background: "linear-gradient(45deg, #5e1687 30%, #d8326b 90%)",
                },
                '&.Mui-disabled': {
                    background: "#e0e0e0",
                }
              }}
            >
              <Send />
            </Button>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default Chatbot;